import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react'
import { toast } from 'react-hot-toast'
import { AdminAuthService } from '@/shared/api'
import type { AdminLoginRequest, AdminUserResponse } from '@/shared/api'

interface AuthContextType {
  user: AdminUserResponse | null
  isLoading: boolean
  login: (credentials: AdminLoginRequest) => Promise<void>
  logout: () => void
  isSuperadmin: boolean
  permissions: string[]
  hasPermission: (section: string, action: string) => boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AdminUserResponse | null>(null)
  const [isLoading, setIsLoading] = useState(true) // Начинаем с true для инициализации
  const [isInitialized, setIsInitialized] = useState(false)

  const login = useCallback(async (credentials: AdminLoginRequest) => {
    try {
      setIsLoading(true)
      console.log('AuthContext.login called with:', credentials)
      
      // Авторизация
      const authResponse = await AdminAuthService.login(credentials)
      console.log('Login successful, token received')
      
      // Сохраняем токен
      localStorage.setItem('admin_token', authResponse.access_token)
      
      // Получаем информацию о пользователе
      const userResponse = await AdminAuthService.getCurrentAdmin()
      console.log('User data received:', userResponse)
      setUser(userResponse)
      
      toast.success('Вход выполнен успешно')
    } catch (error: any) {
      console.error('Login failed:', error)
      // Убираем toast.error отсюда, чтобы избежать дублирования
      // Ошибка будет обработана в LoginPage
      throw error
    } finally {
      setIsLoading(false)
    }
  }, [])

  const logout = useCallback(() => {
    console.log('AuthContext.logout called')
    localStorage.removeItem('admin_token')
    setUser(null)
    toast.success('Выход выполнен')
  }, [])

  const isSuperadmin = !!user?.is_superadmin

  const hasPermission = useCallback((section: string, action: string) => {
    if (isSuperadmin) return true
    return (user?.permissions || []).includes(`${section}.${action}`)
  }, [user?.permissions, isSuperadmin])

  const checkAuth = useCallback(async () => {
    if (isInitialized) {
      console.log('checkAuth: already initialized, skipping')
      return
    }

    const token = localStorage.getItem('admin_token')
    console.log('checkAuth: token exists:', !!token)
    
    if (!token) {
      console.log('checkAuth: no token, user not authenticated')
      setUser(null)
      setIsLoading(false)
      setIsInitialized(true)
      return
    }

    try {
      setIsLoading(true)
      console.log('checkAuth: verifying token...')
      const userResponse = await AdminAuthService.getCurrentAdmin()
      console.log('checkAuth: user verified:', userResponse)
      setUser(userResponse)
    } catch (error) {
      console.error('checkAuth: token invalid:', error)
      localStorage.removeItem('admin_token')
      setUser(null)
    } finally {
      setIsLoading(false)
      setIsInitialized(true)
    }
  }, [isInitialized])

  // Проверяем авторизацию только один раз при инициализации
  useEffect(() => {
    if (!isInitialized) {
      console.log('AuthProvider: initializing...')
      checkAuth()
    }
  }, [checkAuth, isInitialized])

  const value: AuthContextType = {
    user,
    isLoading,
    login,
    logout,
    isSuperadmin,
    permissions: user?.permissions || [],
    hasPermission,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
